import { useEffect, useReducer, useRef, useState } from 'react';
import { getAccessToken } from '@/lib/auth';

/**
 * useRunStream - WebSocket-Hook für Live-Run-Output (Sprint 5, WT-502/WT-506).
 *
 * Backend-Format (siehe app/api/routes/runs.py::stream_run):
 *   { type: "log", sequence, ts, stream, level, line }
 *   { type: "status", status, finished }
 *   { error: "..." }
 *
 * Verhalten:
 *  - Auto-connect bei Mount, auto-disconnect bei Unmount (kein Memory-Leak).
 *  - Auto-reconnect mit exponentialem Backoff (max 5 Versuche, max 8s Delay)
 *    SOFERN der Run noch nicht terminal ist - sobald `finished: true` empfangen
 *    wurde, lassen wir die Verbindung zu (Server schließt).
 *  - Connection-State: 'idle' | 'connecting' | 'open' | 'closed' | 'error'
 *
 * Test-Hook: Aufrufer kann via `wsFactory` einen Mock-Konstruktor injizieren.
 */

export type StreamConnectionState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface RunLogLine {
  /** Lokal vergebene Sequenz-ID, monoton steigend (für key=). */
  seq: number;
  /** Server-seitige Sequenz (aus run_logs.sequence). */
  serverSeq?: number;
  ts: string;
  /** Backend liefert host nicht direkt; ggf. aus metadata. */
  host: string | null;
  /** Ansible-Level. */
  level: 'debug' | 'info' | 'warning' | 'error';
  /** Original-Quelle für Color-Rules. */
  source: 'stdout' | 'stderr' | 'event';
  /** Ansible-Event-Name (host_ok, host_failed, ...) falls aus Stream-Metadata. */
  event?: string;
  message: string;
}

interface State {
  lines: RunLogLine[];
  /** Status (Backend-string), z.B. running|success|failed|… */
  status: string | null;
  finished: boolean;
  connection: StreamConnectionState;
  reconnectAttempts: number;
}

type Action =
  | { type: 'connecting' }
  | { type: 'open' }
  | { type: 'closed' }
  | { type: 'error' }
  | { type: 'log'; line: RunLogLine }
  | { type: 'status'; status: string; finished: boolean }
  | { type: 'reconnect_attempt' }
  | { type: 'reset' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'connecting':
      return { ...state, connection: 'connecting' };
    case 'open':
      return { ...state, connection: 'open', reconnectAttempts: 0 };
    case 'closed':
      return { ...state, connection: 'closed' };
    case 'error':
      return { ...state, connection: 'error' };
    case 'log':
      // Cap auf 5000 Zeilen damit sich der Browser nicht hängt.
      return {
        ...state,
        lines:
          state.lines.length >= 5000
            ? [...state.lines.slice(state.lines.length - 4999), action.line]
            : [...state.lines, action.line],
      };
    case 'status':
      return { ...state, status: action.status, finished: action.finished };
    case 'reconnect_attempt':
      return { ...state, reconnectAttempts: state.reconnectAttempts + 1 };
    case 'reset':
      return initialState();
  }
}

function initialState(): State {
  return {
    lines: [],
    status: null,
    finished: false,
    connection: 'idle',
    reconnectAttempts: 0,
  };
}

export interface UseRunStreamOptions {
  /** WebSocket-Konstruktor injizieren (Tests). Default: globaler WebSocket. */
  wsFactory?: (url: string) => WebSocket;
  /** Max. Reconnects (Default 5). */
  maxReconnects?: number;
  /** Pause via Aufrufer (Logs anzeigen aber WS nicht weiter offen halten). */
  paused?: boolean;
}

export interface UseRunStreamResult extends State {
  /** Manuell reconnecten (nach 'closed' / 'error'). */
  reconnect: () => void;
  /** Hard-Reset des Buffers. */
  clear: () => void;
}

/**
 * URL-Builder. Token-Auth via query-param, weil Browser-WebSocket keine
 * Custom-Header erlaubt. Backend prüft Token aktuell noch NICHT (Single-User-
 * Tool mit 127.0.0.1-Bind), aber wir senden ihn proaktiv mit.
 */
function buildStreamUrl(runId: string): string {
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined ?? '/api/v1')
    .replace(/\/$/, '');
  const protocol =
    typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss' : 'ws';
  const host = typeof window !== 'undefined' ? window.location.host : 'localhost:5173';
  const token = getAccessToken();
  const tokenSuffix = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${protocol}://${host}${apiBase}/runs/${encodeURIComponent(runId)}/stream${tokenSuffix}`;
}

export function useRunStream(
  runId: string | undefined,
  options: UseRunStreamOptions = {}
): UseRunStreamResult {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  // Zähler, den reconnect() erhöht. Steht in den Effekt-Dependencies, damit
  // ein manueller reconnect() den Verbindungs-Effekt komplett neu startet
  // (frischer attempt=0). Ohne diesen Trigger blieb der Stream nach
  // maxReconnects still tot - reset() allein hat den Effekt nie erneut gefeuert.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const seqRef = useRef(0);
  const stoppedRef = useRef(false);
  const maxReconnects = options.maxReconnects ?? 5;
  // Factory aus Ref zugreifen damit useEffect-Dep stabil ist.
  const wsFactoryRef = useRef(options.wsFactory);
  wsFactoryRef.current = options.wsFactory;

  useEffect(() => {
    if (!runId || options.paused) {
      stoppedRef.current = true;
      return;
    }
    stoppedRef.current = false;
    let attempt = 0;

    const connect = (): void => {
      if (stoppedRef.current) return;
      dispatch({ type: 'connecting' });
      let ws: WebSocket;
      try {
        const factory = wsFactoryRef.current ?? ((url: string) => new WebSocket(url));
        ws = factory(buildStreamUrl(runId));
      } catch {
        dispatch({ type: 'error' });
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = (): void => {
        dispatch({ type: 'open' });
      };

      ws.onmessage = (event: MessageEvent<unknown>): void => {
        const raw = typeof event.data === 'string' ? event.data : '';
        if (!raw) return;
        let msg: StreamMessage | null;
        try {
          msg = JSON.parse(raw) as StreamMessage;
        } catch {
          return;
        }
        const finished = handleMessage(msg, dispatch, () => ++seqRef.current);
        if (finished) {
          // Server signalisiert Ende, kein Reconnect mehr.
          stoppedRef.current = true;
        }
      };

      ws.onerror = (): void => {
        dispatch({ type: 'error' });
      };

      ws.onclose = (): void => {
        dispatch({ type: 'closed' });
        if (!stoppedRef.current) {
          scheduleReconnect();
        }
      };
    };

    const scheduleReconnect = (): void => {
      if (stoppedRef.current) return;
      if (attempt >= maxReconnects) return;
      attempt += 1;
      dispatch({ type: 'reconnect_attempt' });
      const delay = Math.min(8_000, 500 * 2 ** attempt);
      reconnectTimer.current = window.setTimeout(connect, delay);
    };

    connect();

    return () => {
      stoppedRef.current = true;
      if (reconnectTimer.current) {
        window.clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && ws.readyState <= WebSocket.OPEN) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [runId, options.paused, maxReconnects, reconnectNonce]);

  return {
    ...state,
    reconnect: () => {
      // Buffer leeren (Server re-streamt ab since_seq=0, sonst Duplikate) und
      // den Verbindungs-Effekt via Nonce neu auslösen.
      stoppedRef.current = false;
      dispatch({ type: 'reset' });
      setReconnectNonce((n) => n + 1);
    },
    clear: () => dispatch({ type: 'reset' }),
  };
}

// ---------------------------------------------------------------------------
// Message-Handler (extrahiert für Tests)
// ---------------------------------------------------------------------------

export interface StreamMessage {
  type?: 'log' | 'status' | 'heartbeat';
  sequence?: number;
  ts?: string;
  /** stream: stdout|stderr|event (Backend-string). */
  stream?: string;
  /** level: debug|info|warning|error (Backend-string). */
  level?: string;
  line?: string;
  /** Status: running|success|… (Backend-string). */
  status?: string;
  finished?: boolean;
  /** Backend-Error-Marker. */
  error?: string;
  metadata?: Record<string, unknown> | null;
}

/**
 * Pure Message-Handler-Funktion - exported für Unit-Tests.
 * Liefert true wenn die WS-Verbindung danach beendet werden soll.
 */
export function handleMessage(
  msg: StreamMessage | null,
  dispatch: React.Dispatch<Action>,
  nextSeq: () => number
): boolean {
  if (!msg) return false;
  if (msg.type === 'status') {
    if (msg.status) {
      dispatch({
        type: 'status',
        status: msg.status,
        finished: Boolean(msg.finished),
      });
    }
    return Boolean(msg.finished);
  }
  if (msg.type === 'log') {
    const stream = msg.stream ?? 'stdout';
    const level = normalizeLevel(msg.level);
    const source: RunLogLine['source'] =
      stream === 'stderr' ? 'stderr' : stream === 'event' ? 'event' : 'stdout';
    const meta = msg.metadata ?? {};
    const host = typeof meta.host === 'string' ? meta.host : null;
    const event = typeof meta.event === 'string' ? meta.event : undefined;
    const newLine: RunLogLine = {
      seq: nextSeq(),
      ts: msg.ts ?? new Date().toISOString(),
      host,
      level,
      source,
      message: msg.line ?? '',
    };
    if (msg.sequence !== undefined) newLine.serverSeq = msg.sequence;
    if (event !== undefined) newLine.event = event;
    dispatch({ type: 'log', line: newLine });
    return false;
  }
  if (msg.error) {
    // Server hat protokolliert dass run nicht existiert / Permission etc.
    dispatch({ type: 'error' });
    return true;
  }
  return false;
}

const VALID_LEVELS = new Set(['debug', 'info', 'warning', 'error']);

function normalizeLevel(level: string | undefined): RunLogLine['level'] {
  if (level && VALID_LEVELS.has(level)) return level as RunLogLine['level'];
  return 'info';
}
